import { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const VoiceContext = createContext(null);
export const useVoice = () => useContext(VoiceContext);

const VOICE_CATEGORIES = [
  {
    id: "warm",
    label: "Warm & Nurturing",
    desc: "Gentle, caring tones",
    voices: [
      { key: "coral", label: "Coral", desc: "Warm, inviting", gender: "Female", origin: "Global" },
      { key: "sage", label: "Sage", desc: "Wise, measured", gender: "Neutral", origin: "Global" },
    ],
  },
  {
    id: "clear",
    label: "Clear & Confident",
    desc: "Bright, assertive delivery",
    voices: [
      { key: "nova", label: "Nova", desc: "Energetic, upbeat", gender: "Female", origin: "American" },
      { key: "alloy", label: "Alloy", desc: "Balanced, neutral", gender: "Neutral", origin: "International" },
    ],
  },
  {
    id: "smooth",
    label: "Smooth & Soothing",
    desc: "Calm, relaxing presence",
    voices: [
      { key: "echo", label: "Echo", desc: "Smooth, calm", gender: "Male", origin: "European" },
      { key: "shimmer", label: "Shimmer", desc: "Bright, cheerful", gender: "Female", origin: "British" },
    ],
  },
  {
    id: "deep",
    label: "Deep & Grounding",
    desc: "Authoritative, reassuring",
    voices: [
      { key: "onyx", label: "Onyx", desc: "Deep, commanding", gender: "Male", origin: "African" },
      { key: "ash", label: "Ash", desc: "Steady, clear", gender: "Male", origin: "Australian" },
    ],
  },
  {
    id: "expressive",
    label: "Expressive & Cultural",
    desc: "Rich, dramatic storytelling",
    voices: [
      { key: "fable", label: "Fable", desc: "Storytelling, dramatic", gender: "Neutral", origin: "South Asian" },
    ],
  },
];

export { VOICE_CATEGORIES };

function base64ToBlob(base64, contentType) {
  const byteCharacters = atob(base64);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(new Uint8Array(byteNumbers));
  }
  return new Blob(byteArrays, { type: contentType });
}

export function VoiceProvider({ children }) {
  const { token, user, updateUser } = useAuth();
  const [voice, setVoiceState] = useState(() => localStorage.getItem("omni_voice") || "sage");
  const [voiceOnboarded, setVoiceOnboardedState] = useState(() => localStorage.getItem("omni_voice_onboarded") === "true");
  const [ttsEnabled, setTtsEnabledState] = useState(() => {
    const stored = localStorage.getItem("omni_tts");
    return stored === null ? true : stored === "true"; // ON by default
  });
  const [speaking, setSpeaking] = useState(false);
  const [speakingMsgId, setSpeakingMsgId] = useState(null);
  const [ttsStatus, setTtsStatus] = useState(null);
  const audioRef = useRef(null);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Fetch TTS status on mount and after add-on changes
  const fetchTtsStatus = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/voice/tts-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTtsStatus(data);
    } catch (err) {
      // Silently fail - TTS status is non-critical
    }
  }, [token]);

  useEffect(() => {
    fetchTtsStatus();
  }, [fetchTtsStatus]);

  // Server preferences (account-wide) win over this device's local cache once loaded
  useEffect(() => {
    if (user?.preferences?.voice) {
      setVoiceState(user.preferences.voice);
      localStorage.setItem("omni_voice", user.preferences.voice);
    }
    if (user?.preferences?.voice_onboarded) {
      setVoiceOnboardedState(true);
      localStorage.setItem("omni_voice_onboarded", "true");
    }
  }, [user?.preferences?.voice, user?.preferences?.voice_onboarded]);

  const persistPreference = useCallback((key, value) => {
    if (!token) return;
    axios.put(`${API}/settings/profile`, { [key]: value }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  }, [token]);

  const setVoice = (v) => {
    setVoiceState(v);
    localStorage.setItem("omni_voice", v);
    persistPreference("voice", v);
    updateUser({ preferences: { ...(user?.preferences || {}), voice: v } });
  };

  const dismissVoiceOnboarding = useCallback(() => {
    setVoiceOnboardedState(true);
    localStorage.setItem("omni_voice_onboarded", "true");
    persistPreference("voice_onboarded", true);
    updateUser({ preferences: { ...(user?.preferences || {}), voice_onboarded: true } });
  }, [persistPreference, updateUser, user]);

  const setTtsEnabled = (val) => {
    setTtsEnabledState(val);
    localStorage.setItem("omni_tts", String(val));
    if (!val && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setSpeaking(false);
      setSpeakingMsgId(null);
    }
  };

  const toggleTts = () => setTtsEnabled(!ttsEnabled);

  // context: "wisdom" | "regular"
  const speakText = useCallback(async (text, msgId = null, voiceOverride = null, context = "regular") => {
    if (!text || !token) return;
    // Stop any current playback first
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    // OpenAI TTS max is 4096 chars — truncate gracefully at sentence boundary
    let safeText = String(text).trim();
    if (safeText.length > 4000) {
      const cutoff = safeText.lastIndexOf(".", 3950);
      safeText = cutoff > 2000 ? safeText.slice(0, cutoff + 1) : safeText.slice(0, 3950) + "...";
    }
    const useVoice = voiceOverride || voice;
    setSpeaking(true);
    setSpeakingMsgId(msgId);
    try {
      const { data } = await axios.post(
        `${API}/voice/synthesize`,
        { text: safeText, voice: useVoice, context },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );

      // Handle TTS limit errors (returned as 200 with error field)
      if (data.error === "tts_limit_reached") {
        setSpeaking(false);
        setSpeakingMsgId(null);
        toast(data.message || "Wisdom Council voice limit reached", {
          description: "Upgrade your plan for more voice responses",
          duration: 5000,
        });
        fetchTtsStatus(); // refresh status
        return;
      }
      if (data.error === "tts_addon_required") {
        setSpeaking(false);
        setSpeakingMsgId(null);
        toast("Voice add-on required", {
          description: data.message || "Get Voice Unlimited for $9.99/mo to hear all responses",
          duration: 5000,
          action: {
            label: "See Plans",
            onClick: () => window.location.href = "/pricing",
          },
        });
        return;
      }

      if (!data.audio) throw new Error("No audio in response");
      const audioBlob = base64ToBlob(data.audio, "audio/mp3");
      const url = URL.createObjectURL(audioBlob);
      const audio = new Audio(url);
      audio.volume = 1.0;
      audioRef.current = audio;
      audio.onended = () => {
        setSpeaking(false);
        setSpeakingMsgId(null);
        URL.revokeObjectURL(url);
        audioRef.current = null;
      };
      audio.onerror = () => {
        setSpeaking(false);
        setSpeakingMsgId(null);
        URL.revokeObjectURL(url);
        audioRef.current = null;
        toast.error("Audio playback failed — try again");
      };
      await audio.play();
      // Refresh TTS status after successful play
      fetchTtsStatus();
    } catch (err) {
      console.error("TTS failed:", err);
      setSpeaking(false);
      setSpeakingMsgId(null);
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        toast.error("Voice generation timed out — try a shorter message");
      } else {
        toast.error("Voice playback failed — check your connection");
      }
    }
  }, [token, voice, fetchTtsStatus]);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setSpeaking(false);
    setSpeakingMsgId(null);
  }, []);

  // Transcribe audio blob to text (STT - free for all tiers)
  const transcribeAudio = useCallback(async (blob, mimeType) => {
    if (!token) return null;
    try {
      const ext = mimeType.includes("mp4") ? "mp4" : "webm";
      const formData = new FormData();
      formData.append("file", blob, `recording.${ext}`);
      const { data } = await axios.post(`${API}/voice/transcribe`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      if (data.text && data.text.trim()) {
        return data.text.trim();
      }
      return null;
    } catch (err) {
      console.error("Transcription error:", err);
      return null;
    }
  }, [token]);

  // Get the flat list of all voices
  const allVoices = VOICE_CATEGORIES.flatMap(c => c.voices);
  const currentVoiceInfo = allVoices.find(v => v.key === voice) || allVoices[0];

  return (
    <VoiceContext.Provider
      value={{
        voice,
        setVoice,
        voiceOnboarded,
        dismissVoiceOnboarding,
        ttsEnabled,
        setTtsEnabled,
        toggleTts,
        speaking,
        speakingMsgId,
        speakText,
        stopSpeaking,
        transcribeAudio,
        currentVoiceInfo,
        allVoices,
        ttsStatus,
        fetchTtsStatus,
      }}
    >
      {children}
    </VoiceContext.Provider>
  );
}
